import React, { useEffect, useState } from 'react';
import {
  Container,
  Typography,
  Box,
  TextField,
  Button,
  Grid,
  Card,
  CardContent,
  Snackbar,
  Alert,
  Fab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import { useAuth } from '../AuthContext';
import { fetchUserRods, fetchUserBaits, createUserRod, createUserBait, updateUserRod, updateUserBait, deleteUserRod, deleteUserBait } from '../api';
import BackButton from '../BackButton';

export default function TacklePage() {
  const { token } = useAuth();
  const [rods, setRods] = useState([]);
  const [baits, setBaits] = useState([]);
  const [loading, setLoading] = useState(false);
  const [rodDialogOpen, setRodDialogOpen] = useState(false);
  const [baitDialogOpen, setBaitDialogOpen] = useState(false);
  const [editingRod, setEditingRod] = useState(null);
  const [editingBait, setEditingBait] = useState(null);
  const [rodForm, setRodForm] = useState({ name: '', length_m: '', casting_weight: '' });
  const [baitForm, setBaitForm] = useState({ name: '' });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const showMessage = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  useEffect(() => {
    if (!token) return;
    const sortByOrderAndName = (items) =>
      (Array.isArray(items) ? items : []).slice().sort((a, b) => {
        const orderA = typeof a.order === 'number' ? a.order : 0;
        const orderB = typeof b.order === 'number' ? b.order : 0;
        if (orderA !== orderB) return orderA - orderB;
        return String(a.name || '').localeCompare(String(b.name || ''));
      });

    const load = async () => {
      setLoading(true);
      try {
        const [rodsData, baitsData] = await Promise.all([
          fetchUserRods(token),
          fetchUserBaits(token),
        ]);
        setRods(sortByOrderAndName(rodsData));
        setBaits(sortByOrderAndName(baitsData));
      } catch (err) {
        showMessage('Fehler beim Laden der Ausrüstung', 'error');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [token]);

  const updateListItem = (listSetter) => (id, field, value) => {
    listSetter((prev) => prev.map((item) => (item.id === id ? { ...item, [field]: value } : item)));
  };

  const handleDeleteRod = async (id) => {
    try {
      await deleteUserRod(token, id);
      setRods((prev) => prev.filter((r) => r.id !== id));
      showMessage('Rute gelöscht');
    } catch (err) {
      showMessage('Fehler beim Löschen der Rute', 'error');
    }
  };

  const handleDeleteBait = async (id) => {
    try {
      await deleteUserBait(token, id);
      setBaits((prev) => prev.filter((b) => b.id !== id));
      showMessage('Köder gelöscht');
    } catch (err) {
      showMessage('Fehler beim Löschen des Köders', 'error');
    }
  };

  const updateRodField = updateListItem(setRods);
  const updateBaitField = updateListItem(setBaits);

  const openNewRodDialog = () => {
    setEditingRod(null);
    setRodForm({ name: '', length_m: '', casting_weight: '' });
    setRodDialogOpen(true);
  };

  const openEditRodDialog = (rod) => {
    setEditingRod(rod);
    setRodForm({
      name: rod.name || '',
      length_m: rod.length_m ?? '',
      casting_weight: rod.casting_weight || '',
    });
    setRodDialogOpen(true);
  };

  const saveRodDialog = async () => {
    if (!rodForm.name.trim()) return;
    try {
      if (editingRod) {
        const payload = {
          name: rodForm.name.trim(),
          order: editingRod.order,
          is_active: editingRod.is_active,
          length_m: rodForm.length_m === '' ? null : Number(rodForm.length_m),
          casting_weight: rodForm.casting_weight || null,
        };
        const updated = await updateUserRod(token, editingRod.id, payload);
        setRods((prev) => prev.map((r) => (r.id === editingRod.id ? { ...r, ...updated } : r)));
        showMessage('Rute gespeichert');
      } else {
        const order = rods.length;
        const payload = {
          name: rodForm.name.trim(),
          order,
          is_active: true,
          length_m: rodForm.length_m === '' ? null : Number(rodForm.length_m),
          casting_weight: rodForm.casting_weight || null,
        };
        const created = await createUserRod(token, payload);
        setRods((prev) => [...prev, created].sort((a, b) => (a.order - b.order) || a.name.localeCompare(b.name)));
        showMessage('Rute hinzugefügt');
      }
      setRodDialogOpen(false);
    } catch (err) {
      showMessage('Fehler beim Speichern der Rute', 'error');
    }
  };

  const openNewBaitDialog = () => {
    setEditingBait(null);
    setBaitForm({ name: '' });
    setBaitDialogOpen(true);
  };

  const openEditBaitDialog = (bait) => {
    setEditingBait(bait);
    setBaitForm({ name: bait.name || '' });
    setBaitDialogOpen(true);
  };

  const saveBaitDialog = async () => {
    if (!baitForm.name.trim()) return;
    try {
      if (editingBait) {
        const payload = {
          name: baitForm.name.trim(),
          order: editingBait.order,
          is_active: editingBait.is_active,
        };
        const updated = await updateUserBait(token, editingBait.id, payload);
        setBaits((prev) => prev.map((b) => (b.id === editingBait.id ? { ...b, ...updated } : b)));
        showMessage('Köder gespeichert');
      } else {
        const order = baits.length;
        const payload = {
          name: baitForm.name.trim(),
          order,
          is_active: true,
        };
        const created = await createUserBait(token, payload);
        setBaits((prev) => [...prev, created].sort((a, b) => (a.order - b.order) || a.name.localeCompare(b.name)));
        showMessage('Köder hinzugefügt');
      }
      setBaitDialogOpen(false);
    } catch (err) {
      showMessage('Fehler beim Speichern des Köders', 'error');
    }
  };

  return (
    <Container maxWidth="md" sx={{ mt: 4 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 3,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <BackButton />
          <Typography variant="h5">
            Meine Ausrüstung
          </Typography>
        </Box>
      </Box>

      <Box sx={{ mt: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h6">
            Ruten
          </Typography>
          <Fab
            color="primary"
            size="small"
            aria-label="Neue Rute"
            onClick={openNewRodDialog}
          >
            <AddIcon fontSize="small" />
          </Fab>
        </Box>
        <Grid container spacing={2}>
          {rods.map((r) => (
            <Grid item xs={12} sm={6} md={4} key={r.id}>
              <Card sx={{ borderRadius: 3 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {r.name}
                    </Typography>
                    <IconButton size="small" onClick={() => openEditRodDialog(r)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Box>
                  {(r.length_m != null || r.casting_weight) && (
                    <Typography variant="body2" color="text.secondary">
                      {r.length_m != null && `Länge: ${r.length_m} m`}
                      {r.length_m != null && r.casting_weight ? ' · ' : ''}
                      {r.casting_weight && `Wurfgewicht: ${r.casting_weight}`}
                    </Typography>
                  )}
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                    <Button size="small" color="error" onClick={() => handleDeleteRod(r.id)}>
                      Löschen
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>

      <Box sx={{ mt: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h6">
            Köder
          </Typography>
          <Fab
            color="primary"
            size="small"
            aria-label="Neuer Köder"
            onClick={openNewBaitDialog}
          >
            <AddIcon fontSize="small" />
          </Fab>
        </Box>
        <Grid container spacing={2}>
          {baits.map((b) => (
            <Grid item xs={12} sm={6} md={4} key={b.id}>
              <Card sx={{ borderRadius: 3 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {b.name}
                    </Typography>
                    <IconButton size="small" onClick={() => openEditBaitDialog(b)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                    <Button size="small" color="error" onClick={() => handleDeleteBait(b.id)}>
                      Löschen
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Rod dialog */}
      <Dialog
        open={rodDialogOpen}
        onClose={() => setRodDialogOpen(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>{editingRod ? 'Rute bearbeiten' : 'Neue Rute'}</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Name"
              value={rodForm.name}
              onChange={(e) => setRodForm((prev) => ({ ...prev, name: e.target.value }))}
              fullWidth
            />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Länge (m)"
                type="number"
                value={rodForm.length_m}
                onChange={(e) => setRodForm((prev) => ({ ...prev, length_m: e.target.value }))}
                fullWidth
              />
              <TextField
                label="Wurfgewicht"
                value={rodForm.casting_weight}
                onChange={(e) => setRodForm((prev) => ({ ...prev, casting_weight: e.target.value }))}
                fullWidth
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRodDialogOpen(false)}>Abbrechen</Button>
          <Button onClick={saveRodDialog} variant="contained" disabled={!rodForm.name.trim() || loading}>
            Speichern
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bait dialog */}
      <Dialog
        open={baitDialogOpen}
        onClose={() => setBaitDialogOpen(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>{editingBait ? 'Köder bearbeiten' : 'Neuer Köder'}</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <TextField
            label="Name"
            value={baitForm.name}
            onChange={(e) => setBaitForm({ name: e.target.value })}
            fullWidth
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBaitDialogOpen(false)}>Abbrechen</Button>
          <Button onClick={saveBaitDialog} variant="contained" disabled={!baitForm.name.trim() || loading}>
            Speichern
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
      >
        <Alert severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}
